import { Box } from '@mui/material';

import { type HandoverSpec } from '@/designs-dev/handover/types';
import LeaveCover from '@/cam-admin/care-circles/LeaveCover';
import ScheduleCoverDrawer from '@/cam-admin/care-circles/ScheduleCoverDrawer';
import {
  COVER_PERIODS,
  type CoverPeriod,
} from '@/cam-admin/care-circles/LeaveCover.fixtures';

// The Caseloads board, rendered on the Gardenia content column with the
// data it needs for each state.
const board = (periods?: CoverPeriod[]) => () => (
  <Box
    sx={{
      height: '100%',
      p: 3,
      bgcolor: 'background.default',
      overflow: 'auto',
    }}
  >
    <LeaveCover periods={periods} />
  </Box>
);

// The Set your cover drawer, shown open over the board. `clash` prefills dates
// that overlap existing leave, to show the error state.
const scheduleDrawer =
  (clash = false) =>
  () => (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      <ScheduleCoverDrawer
        open
        existingPeriods={COVER_PERIODS}
        initial={
          clash
            ? {
                start: '2026-07-10',
                end: '2026-07-12',
                mainCover: 'Amara Osei',
                handoverNote: '',
              }
            : undefined
        }
        onClose={() => undefined}
        onSubmit={() => undefined}
      />
    </Box>
  );

const upcomingOne = COVER_PERIODS.filter((p) => p.id === 'cp-up-1');
const pastOnly = COVER_PERIODS.filter((p) => p.status === 'past');

/**
 * CAM-side handover for Cover: a CAM schedules cover for when they're away, and
 * assigns a colleague to each of their circles so nothing goes quiet while
 * they're off. The tab shows the whole team's leave, so it answers both
 * directions — yours, and the circles you are covering. Part of the Care
 * Recipient Control Panel v3 (Notifications) work — the notifications are the
 * point, so they're specified in full below.
 */
export const leaveCoverSpec: HandoverSpec = {
  id: 'caseloads',
  title: 'Cover',
  screen: 'desktop',
  description:
    'A CAM books a cover period for when they’re away. Every circle defaults to a single main cover, and the CAM can pick a different colleague for any circle. So a circle is never uncovered. While the CAM is off, cover holds their circles and the right people are notified.',
  links: [
    {
      label: 'Project: Control Panel v3 — Notifications (Linear)',
      url: 'https://linear.app/karehero/project/care-recipient-control-panel-v3-notifications-c1d40cc4f9db/overview',
    },
    {
      label: 'Live prototype (Care circles › Cover)',
      url: '/care-circles',
    },
  ],
  tables: [
    {
      title: 'Cover actions',
      caption:
        'What each control does. Actions are mocked in the prototype — behaviour and confirmations to be built.',
      columns: ['Action', 'Where', 'What it should do'],
      rows: [
        [
          'Schedule cover',
          'Header, every tab',
          'Open the Set your cover flow: dates, main cover, handover note, then assign per-circle cover.',
        ],
        [
          'Open a period',
          'Any row, either tab',
          'Opens the cover drawer. Your own leave is editable; leave you are covering is read-only.',
        ],
        [
          'Change a circle’s Cover',
          'Cover drawer → per-row picker',
          'Override the main cover for one circle. Saves immediately and notifies the new (and previous) colleague.',
        ],
        [
          'Cancel this leave',
          'Cover drawer, upcoming period',
          'Drops the period. Needs a confirm, and should notify the covers it releases.',
        ],
        [
          'Cancel',
          'Upcoming period',
          'Remove the period, with a confirm. Notifies anyone who was assigned.',
        ],
      ],
    },
    {
      title: 'Notifications',
      caption:
        'The point of the feature — none of these are wired in the prototype yet.',
      columns: ['Event', 'Who’s notified', 'When', 'In v1?'],
      rows: [
        [
          'Cover scheduled',
          'Main cover + each assigned colleague',
          'On save',
          'Yes',
        ],
        [
          'Circle cover assigned / changed',
          'Newly assigned colleague (and the removed one)',
          'On save',
          'Yes',
        ],
        [
          'Cover starting soon',
          'Main cover + assigned colleagues',
          'Day before start',
          'TBD',
        ],
        ['Cover ended early', 'Affected colleagues', 'On end', 'Yes'],
        ['Cover cancelled', 'Affected colleagues', 'On cancel', 'Yes'],
        [
          'Who’s covering shown to the family',
          'The care circle (member app)',
          'During the period',
          'Open question',
        ],
      ],
    },
  ],
  flows: [
    {
      title: 'Schedule cover',
      steps: [
        {
          id: 'board',
          title: 'Cover',
          tap: 'Care circles › Cover',
          render: board(),
        },
        {
          id: 'set',
          title: 'Set your cover',
          tap: 'Tap ‘Schedule cover’',
          render: scheduleDrawer(),
        },
        {
          id: 'assign',
          title: 'Assign cover (to build)',
          tap: 'Continue → override the main cover per circle, then confirm',
          render: board(upcomingOne),
        },
      ],
    },
    {
      title: 'Edit upcoming cover',
      steps: [
        {
          id: 'expand',
          title: 'The table',
          tap: 'Period, duration, cover and status, one row each',
          render: board(),
        },
        {
          id: 'edit',
          title: 'Open a row',
          tap: 'Click a period → change dates, main cover, note, per-circle cover',
          render: scheduleDrawer(),
        },
      ],
    },
  ],
  states: [
    {
      id: 'happy',
      title: 'Active, upcoming & past',
      kind: 'happy',
      repro: 'Caseloads with cover in all three sections',
      render: board(),
    },
    {
      id: 'empty',
      title: 'No cover scheduled',
      kind: 'empty',
      repro: 'A CAM who has never scheduled cover',
      render: board([]),
    },
    {
      id: 'past-only',
      title: 'Past only',
      kind: 'edge',
      repro: 'Nothing active or upcoming, only history',
      render: board(pastOnly),
    },
    {
      id: 'overlap',
      title: 'Dates clash with booked leave',
      kind: 'error',
      repro: 'Pick dates in Set your cover that overlap an existing period',
      render: scheduleDrawer(true),
    },
  ],
  requirements: [
    {
      text: 'Cover periods grouped into active / upcoming / past',
      status: 'partial',
      where: 'Board',
      note: 'Status is hand-set in the prototype; it must be derived from real start/end dates vs. now.',
    },
    {
      text: 'Period table: period, duration, cover, status — one row per period',
      status: 'built',
      where: 'LeaveCover',
    },
    {
      text: 'My leave and Cover as two tabs, the same periods read from either end',
      status: 'built',
      where: 'Care circles tabs',
    },
    {
      text: 'Per-circle Cover picker that defaults to the main cover, overridable per circle',
      status: 'partial',
      where: 'CoverSelect',
      note: 'Eligible-colleague list is static; the default-to-main-cover is implied by the data, not enforced in one place.',
    },
    {
      text: 'Handover note captured and shown on the period',
      status: 'built',
      where: 'Cover drawer',
      note: 'Captured and read back in the drawer, including read-only for the colleague covering.',
    },
    {
      text: 'Empty state (no cover scheduled)',
      status: 'built',
      where: 'Empty state',
    },
    {
      text: 'Schedule cover flow: dates + main cover + handover note, then assign per circle',
      status: 'partial',
      where: 'Set your cover',
      note: 'A new period has no circles to assign until it is saved, so the per-circle step only appears on reopening. No confirmation screen.',
    },
    {
      text: 'Edit your own period (dates, main cover, note, per-circle cover) and save',
      status: 'partial',
      where: 'Cover drawer',
      note: 'The per-circle picker holds its choice locally — nothing collects it and there is no save for it.',
    },
    {
      text: 'End early, and a confirm on cancelling',
      status: 'not-built',
      note: 'End early is gone with the card it lived on. Cancel drops the period without a confirm.',
    },
    {
      text: 'Notifications on schedule / change / end / cancel',
      status: 'not-built',
      note: 'See the Notifications table — this is the core of the v3 work.',
    },
    {
      text: 'Validation: dates that overlap booked leave, end-before-start',
      status: 'built',
      where: 'Set your cover drawer',
      note: 'A cover who is themselves on leave over the period is not yet checked.',
    },
    {
      text: 'Covering for others — the circles this CAM covers',
      status: 'built',
      note: 'No longer a separate tab. Cover shows the team’s leave, so a period whose main cover is you is marked and reads from the same assignments.',
    },
  ],
  components: {
    mui: [
      'Card',
      'Table',
      'Collapse',
      'Chip',
      'TextField (select)',
      'Drawer (in-tree)',
    ],
    existing: ['Button (llama)', 'Icon (llama)', 'Drawer (wrapped)'],
    new: ['LeaveCover', 'CoverSelect', 'ScheduleCoverDrawer'],
  },
};
